// src/services/db.js
import { openDB } from 'idb';

const DB_NAME = 'erp_offline_db';
const DB_VERSION = 3; // v3 adds generic cached API responses + durable operation IDs

export const openDatabase = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion, transaction) {
      // Drop existing stores if they exist (clean slate)
      if (oldVersion < 2) {
        // Store for pending sync actions
        if (!db.objectStoreNames.contains('pending_actions')) {
          const actionStore = db.createObjectStore('pending_actions', { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          actionStore.createIndex('synced', 'synced');
          actionStore.createIndex('timestamp', 'timestamp');
        }
        
        // Store for offline products
        if (!db.objectStoreNames.contains('products')) {
          const productStore = db.createObjectStore('products', { 
            keyPath: 'productId' 
          });
          productStore.createIndex('name', 'name');
          productStore.createIndex('category', 'category');
        }
        
        // Store for offline orders
        if (!db.objectStoreNames.contains('orders')) {
          const orderStore = db.createObjectStore('orders', { 
            keyPath: 'orderId' 
          });
          orderStore.createIndex('client', 'client');
          orderStore.createIndex('createdAt', 'createdAt');
          orderStore.createIndex('synced', 'synced');
        }
        
        // Store for offline sales
        if (!db.objectStoreNames.contains('sales')) {
          const saleStore = db.createObjectStore('sales', { 
            keyPath: 'saleId', 
            autoIncrement: true 
          });
          saleStore.createIndex('createdAt', 'createdAt');
          saleStore.createIndex('synced', 'synced');
        }
        
        // Store for offline expenses
        if (!db.objectStoreNames.contains('expenses')) {
          const expenseStore = db.createObjectStore('expenses', { 
            keyPath: 'expenseId', 
            autoIncrement: true 
          });
          expenseStore.createIndex('category', 'category');
          expenseStore.createIndex('synced', 'synced');
        }
        
        // Store for sync queue metadata
        if (!db.objectStoreNames.contains('sync_metadata')) {
          db.createObjectStore('sync_metadata', { keyPath: 'key' });
        }
      }
      if (oldVersion < 3 && !db.objectStoreNames.contains('response_cache')) {
        db.createObjectStore('response_cache', { keyPath: 'key' });
      }
    },
  });
};

const currentSessionScope = () => {
  const token = localStorage.getItem('erp_token') || '';
  let hash = 0;
  for (let i = 0; i < token.length; i += 1) hash = ((hash << 5) - hash + token.charCodeAt(i)) | 0;
  return String(hash);
};

// Add action to sync queue
export const queueAction = async (type, data, endpoint, method = 'POST', operationId = null) => {
  const db = await openDatabase();
  const action = {
    type,
    data,
    endpoint,
    method,
    timestamp: Date.now(),
    synced: false,
    retryCount: 0,
    operationId: operationId || (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`),
    sessionScope: currentSessionScope()
  };
  
  const id = await db.add('pending_actions', action);
  console.log(`📝 Queued action: ${type} (ID: ${id})`);
  return id;
};

// Get all pending actions (FIXED)
export const getPendingActions = async () => {
  const db = await openDatabase();
  try {
    // Use getAll with index - only get items where synced is false
    const allActions = await db.getAll('pending_actions');
    const scope = currentSessionScope();
    // Never expose unscoped legacy actions to a newly authenticated account.
    // They remain stored locally but require a separate owner-mediated recovery path.
    const pendingActions = allActions.filter(action => action.synced === false && action.sessionScope === scope);
    return pendingActions;
  } catch (error) {
    console.error('Error getting pending actions:', error);
    return [];
  }
};

// Mark action as synced
export const markActionSynced = async (id) => {
  const db = await openDatabase();
  try {
    const action = await db.get('pending_actions', id);
    if (action) {
      action.synced = true;
      action.syncedAt = Date.now();
      await db.put('pending_actions', action);
      console.log(`✅ Action ${id} marked as synced`);
    }
  } catch (error) {
    console.error(`Error marking action ${id} as synced:`, error);
  }
};

// Delete synced action
export const deleteAction = async (id) => {
  const db = await openDatabase();
  try {
    await db.delete('pending_actions', id);
    console.log(`🗑️ Deleted action ${id}`);
  } catch (error) {
    console.error(`Error deleting action ${id}:`, error);
  }
};

// Save products offline
export const saveProductsOffline = async (products) => {
  const db = await openDatabase();
  const tx = db.transaction('products', 'readwrite');
  for (const product of products) {
    await tx.store.put(product);
  }
  await tx.done;
  console.log(`💾 Saved ${products.length} products offline`);
};

// Get products from offline storage
export const getProductsOffline = async () => {
  const db = await openDatabase();
  try {
    return await db.getAll('products');
  } catch (error) {
    console.error('Error getting offline products:', error);
    return [];
  }
};

// Save single order offline
export const saveOrderOffline = async (order) => {
  const db = await openDatabase();
  await db.put('orders', { ...order, offline: true, synced: false, createdAt: Date.now() });
  console.log(`💾 Saved order ${order.orderId} offline`);
};

// Get offline orders
export const getOfflineOrders = async () => {
  const db = await openDatabase();
  try {
    return await db.getAll('orders');
  } catch (error) {
    console.error('Error getting offline orders:', error);
    return [];
  }
};

// Clear cached/offline presentation data on logout, but retain legacy pending actions for review.
// Pending actions are session-scoped and are never replayed automatically.
export const clearOfflineData = async () => {
  const db = await openDatabase();
  await db.clear('products');
  await db.clear('orders');
  await db.clear('sales');
  await db.clear('expenses');
  await db.clear('sync_metadata');
  console.log('🗑️ Cleared all offline data');
};

// Get sync status
export const getSyncStatus = async () => {
  const pending = await getPendingActions();
  return {
    pendingCount: pending.length,
    isOnline: navigator.onLine,
    lastSync: await getLastSyncTime()
  };
};

// Set last sync time
export const setLastSyncTime = async () => {
  const db = await openDatabase();
  await db.put('sync_metadata', { key: 'lastSyncTime', value: Date.now() });
};

// Get last sync time
export const getLastSyncTime = async () => {
  const db = await openDatabase();
  try {
    const meta = await db.get('sync_metadata', 'lastSyncTime');
    return meta ? meta.value : null;
  } catch (error) {
    return null;
  }
};

export const saveCachedResponse = async (key, data) => {
  const db = await openDatabase();
  await db.put('response_cache', { key, data, cachedAt: Date.now() });
};

export const getCachedResponse = async key => {
  const db = await openDatabase();
  const row = await db.get('response_cache', key);
  return row?.data ?? null;
};
